/** Editor для Вимоги-накладної (списання матеріалів у виробництво, режим 1C_WIP). */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs, nomenclature as nomApi } from '@/api/catalogs';
import { refs as refsApi } from '@/api/refs';
import { departmentsApi } from '@/api/hr';
import { nomenclatureGroups } from '@/api/nomenclatureGroups';
import { accounts as accountsApi } from '@/api/documents';
import DatePopover from '@/components/DatePopover';
import { ProjectField } from '@/components/ProjectSelect';
import {
  materialRequisitions,
  type MaterialRequisitionItemIn,
} from '@/api/materialRequisitions';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import NomenclatureSelect from '@/components/NomenclatureSelect';
import NomenclaturePickerModal from '@/components/NomenclaturePickerModal';
import type { GoodsBalanceRow } from '@/api/documents';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import SearchSelect from '@/components/SearchSelect';
import { useConfirm } from '@/components/ConfirmProvider';
import { createThenPost } from '@/shared/utils/createThenPost';
import TchTable from '@/shared/components/TchTable';

const PERMISSION = 'production.material_transfer.manage';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  source_warehouse_id: string;
  department_id: string;
  nomenclature_group_id: string;
  cost_account_code: string;
  comment: string;
  project_id: string | null;
  items: MaterialRequisitionItemIn[];
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  source_warehouse_id: '',
  department_id: '',
  nomenclature_group_id: '',
  cost_account_code: '',
  comment: '',
  project_id: null,
  items: [],
};


function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}

export default function MaterialRequisitionEditor() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs, totalAccessibleOrgs } = usePermissionsByOrg(PERMISSION);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [tab, setTab] = useState<'items' | 'accounts'>('items');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const warehousesQ = useQuery({
    queryKey: ['warehouses', form.organization_id],
    queryFn: () => refsApi.warehouses(form.organization_id),
    enabled: !!form.organization_id,
  });
  const deptQ = useQuery({
    queryKey: ['departments', form.organization_id],
    queryFn: () => departmentsApi.list({ organization_id: form.organization_id, limit: 500 }),
    enabled: !!form.organization_id,
  });
  const groupQ = useQuery({
    queryKey: ['nomenclature-groups'],
    queryFn: () => nomenclatureGroups.list(),
  });
  const accountsQ = useQuery({
    queryKey: ['accounts-postable'],
    queryFn: () => accountsApi.list({ only_postable: true, limit: 1000 }),
  });
  const nomQ = useQuery({ queryKey: ['nomenclature-full'], queryFn: () => nomApi.listAll() });
  // ч.657: одиниці виміру для колонки «Одиниця» (з картки номенклатури).
  const uomsQ = useQuery({ queryKey: ['uoms-for-picker'], queryFn: refsApi.uoms });
  const docQuery = useQuery({
    queryKey: ['material-requisition', id],
    queryFn: () => materialRequisitions.get(id!),
    enabled: !isNew,
  });

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
  const numberPreview = useNumberPreview('doc.material_requisitions', form.organization_id, isNew);

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: availableOrgs[0]!.id }));
    }
  }, [isNew, availableOrgs, form.organization_id]);
  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        source_warehouse_id: d.source_warehouse_id,
        department_id: d.department_id ?? '',
        nomenclature_group_id: d.nomenclature_group_id ?? '',
        cost_account_code: d.cost_account_code ?? '',
        comment: d.comment ?? '',
        project_id: d.project_id ?? null,
        items: d.items.map((it) => ({
          line_number: it.line_number,
          nomenclature_id: it.nomenclature_id,
          quantity: it.quantity,
          warehouse_id: it.warehouse_id ?? null,
          uom_id: it.uom_id,
          account_code: it.account_code,
        })),
      });
    }
  }, [isNew, docQuery.data]);

  const isPosted = docQuery.data?.posted ?? false;
  // Запобіжник: у спрощеному режимі Вимога-накладна не застосовується (лишала б
  // залишок на рах.23). Блокуємо збереження/проведення + попереджаємо.
  const selectedOrg = orgList.data?.items.find((o) => o.id === form.organization_id);
  const isSimpleMode = selectedOrg?.production_costing_mode === 'SIMPLE';

  const buildPayload = () => ({
    document_number: form.document_number || null,
    document_date: new Date(form.document_date).toISOString(),
    source_warehouse_id: form.source_warehouse_id,
    department_id: form.department_id || null,
    nomenclature_group_id: form.nomenclature_group_id || null,
    cost_account_code: form.cost_account_code || null,
    comment: form.comment || null,
        project_id: form.project_id,
    items: form.items,
  });

  const create = useMutation({
    mutationFn: () => materialRequisitions.create({ organization_id: form.organization_id, ...buildPayload() }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['material-requisitions'] });
      navigate(`/material-requisitions/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => materialRequisitions.update(id!, buildPayload()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['material-requisition', id] });
      qc.invalidateQueries({ queryKey: ['material-requisitions'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const post = useMutation({
    // ч.1266: на новому документі — створити, провести, відкрити створений.
    // ч.1267: на наявному — спершу зберегти правки форми, потім провести.
    mutationFn: async () => {
      if (isNew) {
        return createThenPost(
          () => materialRequisitions.create({ organization_id: form.organization_id, ...buildPayload() }),
          (docId) => materialRequisitions.post(docId),
          (doc) => { qc.invalidateQueries({ queryKey: ['material-requisitions'] }); navigate(`/material-requisitions/${doc.id}`); },
        );
      }
      if (!isPosted) await materialRequisitions.update(id!, buildPayload());
      return materialRequisitions.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['material-requisition', id] });
      qc.invalidateQueries({ queryKey: ['material-requisitions'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => materialRequisitions.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['material-requisition', id] });
      qc.invalidateQueries({ queryKey: ['material-requisitions'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  // ч.703: видалення DRAFT (кнопка на плашці; new/posted — disabled).
  const remove = useMutation({
    mutationFn: () => materialRequisitions.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['material-requisitions'] });
      navigate('/material-requisitions');
    },
    onError: (e) => setError(extractError(e)),
  });

  const uomShortById = useMemo(() => {
    const m = new Map<string, string>();
    (uomsQ.data ?? []).forEach((u) => m.set(u.id, u.short_name));
    return m;
  }, [uomsQ.data]);
  const uomOfRow = (it: MaterialRequisitionItemIn): string => {
    if (it.uom_id) return uomShortById.get(it.uom_id) ?? '';
    const n = nomQ.data?.items.find((x) => x.id === it.nomenclature_id);
    return (n?.base_uom_id && uomShortById.get(n.base_uom_id)) || '';
  };
  // ч.657/658: підбір з попапу (канон реалізації, МУЛЬТИВИБІР) —
  // кількість 1, одиниця з картки.
  function addRowsFromPicker(rows: GoodsBalanceRow[]) {
    setForm((f) => ({
      ...f,
      items: [...f.items, ...rows.map((row, k) => {
        return {
          line_number: f.items.length + 1 + k,
          nomenclature_id: row.nomenclature_id,
          quantity: '1',
          // ч.663: списуємо З ТОГО складу, де товар лежить (рядок попапу).
          warehouse_id: row.warehouse_id || f.source_warehouse_id || null,
          // uom_id НЕ підставляємо: поле — FK на ref.nomenclature_uoms
          // (одиниці КОНКРЕТНОЇ номенклатури), а base_uom_id — класифікатор.
          // NULL = базова одиниця (конвенція); колонка «Одиниця» показує її
          // з картки. Прецедент-фікс: FK violation при збереженні (ч.671).
          uom_id: null,
        };
      })],
    }));
  }
  function removeItem(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i).map((it, idx) => ({ ...it, line_number: idx + 1 })),
    }));
  }
  function setItemField(i: number, patch: Partial<MaterialRequisitionItemIn>) {
    setForm((f) => ({ ...f, items: f.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) }));
  }

  const totalCost = useMemo(
    () =>
      (docQuery.data?.items ?? []).reduce(
        (acc, it) => acc + Number(it.total_cost ?? '0'),
        0,
      ),
    [docQuery.data],
  );

  if (!isNew && docQuery.isLoading) return <div>Завантаження…</div>;

  // Вибір рахунка з плану рахунків (замість ручного вводу — без помилок).
  const acctList = accountsQ.data ?? [];
  const nomName = (nid: string) => {
    const n = nomQ.data?.items.find((x) => x.id === nid);
    return n ? `${n.name}${n.code ? ' — ' + n.code : ''}` : '—';
  };
  const acctItems = acctList.map((a) => ({ id: a.code, label: `${a.code} — ${a.name}`, hint: null }));
  const acctSelect = (
    value: string | null | undefined,
    onChange: (v: string | null) => void,
    def: string,
  ) => (
    <SearchSelect items={acctItems} value={value ?? ''}
      onChange={(v) => onChange(v || null)} disabled={isPosted}
      placeholder={`— (деф. ${def}) —`} />
  );
  function validate(): string | null {
    if (!form.source_warehouse_id) return 'Оберіть склад';
    if (form.items.length === 0) return 'Додайте хоча б один матеріал';
    if (form.items.some((it) => !it.nomenclature_id))
      return 'Оберіть номенклатуру у всіх рядках';
    return null;
  }

  // ч.886 (новий дизайн, Закон #10): «ще дій» не було.
  const railActions: { label: string; title?: string; disabled?: boolean; onClick: () => void }[] = [];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) },
    { id: 'wh', title: 'Склад і аналітика НЗВ', required: 0, filled: 0 },
    { id: 'items', title: 'Матеріали', required: 1, filled: form.items.length > 0 ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нова вимога-накладна' : `Вимога: ${form.document_number}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center',
          flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar
          entriesDoc={{ type: 'doc.material_requisitions', id: isNew ? '' : id! }}
          railMode
          extraActions={railActions}
          onBackToList={() => navigate('/material-requisitions')}
          onSave={() => {
            const err = validate();
            if (err) { setError(err); return; }
            setError(null);
            (isNew ? create.mutate() : update.mutate());
          }}
          saveDisabled={create.isPending || update.isPending || isSimpleMode}
          isPosted={isPosted}
          isNew={isNew}
          onPost={() => {
            const err = validate();
            if (err) { setError(err); return; }
            setError(null);
            post.mutate();
          }}
          postDisabled={post.isPending || isSimpleMode}
          postPending={post.isPending}
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onPrint={() => navigate(`/material-requisitions/${id}/print`)}
          onDelete={() => { void confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }); }}
          deletePending={remove.isPending}
        />
        </div>
      </div>

      {isSimpleMode && (
        <div style={{
          padding: '0.75rem 1rem', margin: '0.5rem 0', borderRadius: 6,
          background: 'var(--badge-amber-bg)', border: '1px solid #fbbf24', color: 'var(--badge-amber-fg)',
        }}>
          ⚠ Ця юрособа веде <b>спрощений</b> облік виробництва (без НЗВ). Вимога-накладна
          тут <b>не застосовується</b> — вона лишила б залишок на рах. 23. Списуйте матеріали
          через <b>Звіт виробництва</b>, або перемкніть облік на «Повний (НЗВ)» у картці організації.
        </div>
      )}

      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew}
          doc={{ type: 'doc.material_requisitions', id: isNew ? '' : id! }} />
      }>
      {/* ч.656 (розкладка власника): [Організація|Номер|Дата|Напрям] →
          [Склад джерело|Підрозділ|Номенкл. група]; ч.886 — блоками. */}
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Організація *</label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
                  disabled={!isNew}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
            </small>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Номер</label>
          <input value={form.document_number}
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''} disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Дата *</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setForm({ ...form, document_date: v })} disabled={isPosted} />
        </div>
        <ProjectField span={2}
            value={form.project_id}
            organizationId={form.organization_id}
            onChange={(v) => setForm({ ...form, project_id: v })}
            disabled={isPosted} />
      </div>
      </DocBlock>

      <DocBlock index={2} title="Склад і аналітика НЗВ">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Склад джерело (за замовчуванням)</label>
          <select value={form.source_warehouse_id}
                  onChange={(e) => setForm({ ...form, source_warehouse_id: e.target.value })}
                  disabled={isPosted}>
            <option value="">—</option>
            {warehousesQ.data?.map((w) => (<option key={w.id} value={w.id}>{w.name} — {w.code}</option>))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Підрозділ (виробництво)</label>
          <select value={form.department_id}
                  onChange={(e) => setForm({ ...form, department_id: e.target.value })}
                  disabled={isPosted}>
            <option value="">— (без аналітики НЗВ)</option>
            {deptQ.data?.items.map((d) => (<option key={d.id} value={d.id}>{d.name}</option>))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Номенклатурна група (одиниця НЗВ)</label>
          <select value={form.nomenclature_group_id}
                  onChange={(e) => setForm({ ...form, nomenclature_group_id: e.target.value })}
                  disabled={isPosted}>
            <option value="">— (без аналітики НЗВ)</option>
            {groupQ.data?.map((g) => (<option key={g.id} value={g.id}>{g.name}</option>))}
          </select>
        </div>
      </div>
      </DocBlock>

      <DocBlock index={3} title="Матеріали"
        actions={!isPosted ? (
          <button type="button" className="primary" onClick={() => setPickerOpen(true)}
            disabled={!form.organization_id}
            title={!form.organization_id ? 'Спершу оберіть організацію' : 'Підбір матеріалу з залишків складу'}>
            + додати номенклатуру
          </button>
        ) : undefined}>
      {/* Вкладки — канон реалізації: ТЧ зліва, «Рахунки обліку» справа. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
        <div className="seg-tabs">
          <button type="button" className={tab === 'items' ? 'active' : ''}
            onClick={() => setTab('items')}>
            Матеріали ({form.items.length}){totalCost > 0 ? ` · ${totalCost.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''}
          </button>
        </div>
        <div className="seg-tabs" style={{ marginLeft: 'auto' }}>
          <button type="button" className={tab === 'accounts' ? 'active' : ''}
            onClick={() => setTab('accounts')}>Рахунки обліку</button>
        </div>
      </div>

      {tab === 'accounts' && (
        <div style={{ marginTop: '0.5rem' }}>
          <div className="doc-grid">
            <div className="field" style={{ gridColumn: 'span 5' }}>
              <label>Рахунок витрат (виробництво)</label>
              {acctSelect(form.cost_account_code, (v) => setForm({ ...form, cost_account_code: v ?? '' }), '23')}
            </div>
          </div>
          <TchTable style={{ marginTop: '0.5rem' }}>
            <thead>
              <tr style={{ textTransform: 'none' }}>
                <th>Номенклатура</th>
                <th style={{ width: 220 }}>Рах. обліку</th>
              </tr>
            </thead>
            <tbody>
              {form.items.filter((it) => it.nomenclature_id).map((it) => {
                const i = form.items.indexOf(it);
                return (
                  <tr key={i}>
                    <td>{nomName(it.nomenclature_id)}</td>
                    <td>{acctSelect(it.account_code, (v) => setItemField(i, { account_code: v }), '201')}</td>
                  </tr>
                );
              })}
            </tbody>
          </TchTable>
          <p style={{ color: 'var(--muted)', fontSize: '0.78rem', marginTop: '0.4rem' }}>
            Порожньо — рахунок за замовчуванням (вказаний у дужках). Оберіть свій, щоб перебити.
          </p>
        </div>
      )}

      {tab === 'items' && (<>
      <TchTable style={{ marginTop: '0.5rem' }}>
        <thead>
          <tr>
            <th style={{ width: 40 }}>#</th>
            <th>Матеріал</th>
            <th style={{ width: 170 }}>Склад</th>
            <th style={{ width: 100 }}>К-сть</th>
            <th style={{ width: 90 }}>Одиниця</th>
            <th style={{ width: 40 }} />
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => (
            <tr key={i}>
              <td>{it.line_number}</td>
              <td>
                <NomenclatureSelect
                  value={it.nomenclature_id}
                  disabled={isPosted}
                  onChange={(v) => setItemField(i, { nomenclature_id: v })}
                  kind="goods"
                />
              </td>
              <td>
                <select value={it.warehouse_id ?? ''}
                        onChange={(e) => setItemField(i, { warehouse_id: e.target.value || null })}
                        disabled={isPosted} style={{ width: '100%' }}>
                  <option value="">— за замовчуванням —</option>
                  {warehousesQ.data?.map((w) => (
                    <option key={w.id} value={w.id}>{w.name} — {w.code}</option>
                  ))}
                </select>
              </td>
              <td>
                <input type="number" step="0.001" value={it.quantity}
                       onChange={(e) => setItemField(i, { quantity: e.target.value })}
                       disabled={isPosted} />
              </td>
              <td style={{ color: 'var(--muted)' }}>{uomOfRow(it)}</td>
              <td>
                {!isPosted && <button onClick={() => removeItem(i)} title="Видалити рядок">×</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </TchTable>
      </>)}

      {pickerOpen && form.organization_id && (
        <NomenclaturePickerModal
          organizationId={form.organization_id}
          documentDate={form.document_date}
          defaultWarehouseId={form.source_warehouse_id || null}
          catalogMode
          expandByWarehouse
          defaultOnlyWithStock
          onlyKind="goods"
          multiSelect
          onPick={(row) => addRowsFromPicker([row])}
          onPickMany={addRowsFromPicker}
          onClose={() => setPickerOpen(false)}
        />
      )}

      </DocBlock>

      <DocBlock index={4} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={form.comment}
                 onChange={(e) => setForm({ ...form, comment: e.target.value })}
                 disabled={isPosted} />
        </div>
        <div>
          {/* Вкладення (2026-08-13, рішення власника: на всіх документах). */}
          <AttachmentsPanel organizationId={form.organization_id}
            documentType="doc.material_requisitions" documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
      {error && <div className="error" style={{ marginTop: '1rem' }}>{error}</div>}
    </div>
  );
}
